import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { MobileDateRangePicker, MobileDateRangePickerProps } from "@mui/x-date-pickers-pro/MobileDateRangePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useEffect, useMemo, useState } from "react";

import { dayjs, Dayjs } from "@holibob-packages/dayjs";
import type { AvailabilityListOptionListItemInput } from "@holibob-packages/graphql-types";
import { generateUUID } from "@holibob-packages/id";

import { useAvailabilityHotelListQuery } from "../../apiHooks/graphql";
import { WarningMessage } from "../../components";
import { useNextTranslation } from "../../hooks";
import { useLanguage } from "../../hooks/useLanguage";
import { HotelRoom } from "./AvailabilityHotelModalHeader";

const DatePickerContainer = styled("div")(({ theme }) => ({
    "& .MuiInputBase-root": {
        backgroundColor: "white",
    },
    "& .MuiInputBase-input:not([disabled])": {
        cursor: "pointer",
    },
    "& .MuiButtonBase-root": {
        margin: "0 0.25rem 0 -0.25rem",
    },
    "& .MuiFormControl-root": {
        marginRight: 0,
        marginLeft: 0,
    },
    "& .MuiStack-root > p": {
        marginRight: theme.spacing(),
        marginLeft: theme.spacing(),
    },
}));

type AvailabilityDateRangePickerProps = {
    productId: string;
    roomList: HotelRoom[];
    onChange: (availabilityListItem: string | null, isDefault: boolean) => void;
} & Omit<MobileDateRangePickerProps<Dayjs>, "value" | "onChange" | "renderInput">;

export const AvailabilityDateRangePicker = ({
    productId,
    onChange,
    roomList = [],
    ...props
}: AvailabilityDateRangePickerProps) => {
    const language = useLanguage();
    const [t] = useNextTranslation("availability");

    const [dateRange, setDateRange] = useState([dayjs().add(7, "day"), dayjs().add(10, "day")]);
    const [checkIn, checkOut] = dateRange;

    const optionList = useMemo(() => {
        const roomCountOption = { id: generateUUID(), type: "ROOM_COUNT", value: `${roomList.length}` };
        const optionList: AvailabilityListOptionListItemInput[] = [
            { id: generateUUID(), type: "CHECK_IN_DATE", value: checkIn.format("YYYY-MM-DD") },
            { id: generateUUID(), type: "CHECK_OUT_DATE", value: checkOut.format("YYYY-MM-DD") },
            roomCountOption,
        ];

        for (const { adultCount, childList } of roomList) {
            optionList.push({
                id: generateUUID(),
                type: "ROOM_ADULT_COUNT",
                parentId: roomCountOption.id,
                value: `${adultCount}`,
            });
            if (childList.length) {
                const roomChildCountOption = {
                    id: generateUUID(),
                    type: "ROOM_CHILD_COUNT",
                    parentId: roomCountOption.id,
                    value: `${childList.length}`,
                };
                optionList.push(roomChildCountOption);
                for (const { age } of childList) {
                    optionList.push({
                        id: generateUUID(),
                        type: "ROOM_CHILD_AGE",
                        parentId: roomChildCountOption.id,
                        value: `${age}`,
                    });
                }
            }
        }

        return optionList;
    }, [roomList, checkIn, checkOut]);

    const { loading, data } = useAvailabilityHotelListQuery({
        variables: {
            productId,
            filter: {
                includeSoldOut: false,
            },
            optionList,
        },
    });

    const availability = useMemo(() => {
        return data?.availabilityList?.nodes ?? [];
    }, [data]);

    const noAvailability = !availability.length && !loading;

    if (noAvailability) {
        onChange(null, true);
    }

    useEffect(() => {
        if (availability.length && availability[0]?.id) {
            onChange(availability[0].id, true);
        }
    }, [availability, onChange]);

    return (
        <>
            <LocalizationProvider adapterLocale={language.codeShort} dateAdapter={AdapterDayjs}>
                <DatePickerContainer>
                    <MobileDateRangePicker
                        value={[checkIn, checkOut]}
                        disabled={loading}
                        format={getDateFormatForLocale(language.codeShort)}
                        renderLoading={() => <CircularProgress size={20} />}
                        {...props}
                        disablePast
                        localeText={{
                            start: t("form.checkin.label"),
                            end: t("form.checkout.label"),
                        }}
                        slotProps={{
                            textField: {
                                InputProps: {
                                    "aria-label": "change date",
                                    fullWidth: true,
                                    startAdornment: (
                                        <IconButton disabled={loading}>
                                            <CalendarTodayOutlinedIcon />
                                        </IconButton>
                                    ),
                                    endAdornment: loading ? <CircularProgress size="1rem" /> : undefined,
                                },
                            },
                        }}
                        onChange={(date) => {
                            const [checkIn, checkOut] = date;
                            if (!checkIn || !checkOut || checkIn.isSame(checkOut)) {
                                return;
                            }

                            setDateRange([checkIn, checkOut]);
                        }}
                    />
                </DatePickerContainer>
            </LocalizationProvider>
            {noAvailability && <WarningMessage align="left" warning={t("message.noAvailabilityRange")} />}
        </>
    );
};

function getDateFormatForLocale(locale: string) {
    const dayjsLocal = dayjs().locale(locale);
    return dayjsLocal
        .localeData()
        .longDateFormat("L")
        .replace(/\u200f/g, "");
}
